import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { industries } from "@/data/industries.data";
import { staggerContainer, fadeUp, viewportOnce } from "@/animations/motion";
import { ArrowUpRight } from "@/icons";
import { cn } from "@/lib/utils";

/** Industries — premium cards with gradient illustration tiles. */
export function Industries() {
  return (
    <Section id="industries">
      <Container>
        <SectionHeading
          eyebrow="Industry Expertise"
          title="Software built around how your industry works"
          description="Every industry has unique processes, compliance requirements and operational challenges. We combine technical expertise with real domain understanding to deliver software that fits the way your business actually operates."
        />

        <motion.div
          variants={staggerContainer(0.07, 0.08)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {industries.map(({ id, name, description, tags, icon: Icon, gradient }) => (
            <motion.div key={id} variants={fadeUp} data-testid={`industry-card-${id}`}>
              <Link
                to="/industries"
                className="group flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-border bg-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-28px_rgba(15,23,42,0.22)]"
              >
                {/* Illustration tile */}
                <div
                  className={cn(
                    "relative flex h-36 items-center justify-center bg-gradient-to-br",
                    gradient,
                  )}
                >
                  <div className="absolute inset-0 bg-grid opacity-40" />
                  <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-white/40 bg-white/70 text-primary shadow-sm backdrop-blur transition-transform duration-500 group-hover:scale-110">
                    <Icon className="h-7 w-7" />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-semibold text-foreground">
                      {name}
                    </h3>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {description}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">

                    {tags && tags.length > 0 && tags.map((tag) => (
                      <span key={tag} className="rounded-full border bg-muted/40 px-3 py-1 text-xs font-medium">
                        {tag}
                      </span>
                    ))}

                  </div>
                  <span className="mt-4 text-sm font-medium text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    Explore {name}
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}
